import { z } from 'zod/v4';
import { OperationalStatusCode } from '../enums/operationalStatusCode';

export const quoteAddressSchema = z.object({
  addressLine1: z.string(),
  subBuilding: z.string().optional(),
  poBox: z.string().optional(),
  street: z.string().optional(),
  number: z.string().optional(),
  concatenatedAddress: z.string().optional(),
  externalAVSAddressId: z.string().optional(),
  isValid: z.boolean(),
  city: z.string(),
  regionOrStateCode: z.string(),
  postalCode: z.string(),
  countryCode: z.string(),
});

export const quoteRequestSchema = z.object({
  customer: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.email(),
    address: quoteAddressSchema,
    phone: z.string().optional(),
  }),
  merchandise: z.array(
    z.object({
      type: z.string(),
      brand: z.string(),
      serialNumber: z.string(),
      modelNumber: z.string(),
      purchaseDate: z.string(),
      purchasePrice: z.string(),
      address: quoteAddressSchema,
    }),
  ),
});

const discountProfileAttributes = z
  .object({
    discountTerm: z.number(),
    discountPercentage: z.number(),
    netAmountBeforeDiscount: z.number(),
    netFirstPaymentAmountAfterPromotionalPeriod: z.number(),
    ccDiscountCodeApplied: z.string().optional(),
    achDiscountCodeApplied: z.string().optional(),
  })
  .optional();

const paymentTypeAttributes = z.object({
  paymentProfileCode: z.string(),
  taxRate: z.number(),
  netTotalAmount: z.number(),
  taxTotalAmount: z.number(),
  netFirstPaymentAmount: z.number(),
  offerPricingKey: z.number(),
  discountProfileAttributes,
});

export const registrationOnlyResponseSchema = z.object({
  personId: z.string(),
  items: z.array(
    z.object({
      id: z.string(),
      operationalStatusAtPos: z.enum(OperationalStatusCode),
      guarantee: z.number(),
      labour: z.number(),
    }),
  ),
});

export const quoteSuccessResponseSchema = z
  .object({
    offer: z.object({
      offerReference: z.string(),
      contracts: z.array(
        z.object({
          contractAttributes: z
            .object({
              productVariantId: z.number(),
              periodOfCover: z.number(),
              levelOfCoverCode: z.string().optional(),
              effectiveDate: z
                .string()
                .regex(
                  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?$/,
                  'effectiveDate must be in YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss format',
                )
                .transform((val) => val.split('T')[0]),
              firstPaymentDate: z
                .string()
                .regex(
                  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?$/,
                  'firstPaymentDate must be in YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss format',
                )
                .transform((val) => val.split('T')[0]),
              contractEndDate: z
                .string()
                .regex(
                  /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?$/,
                  'effectiveDate must be in YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss format',
                )
                .transform((val) => val.split('T')[0])
                .optional(),
              monthly_subs: z.record(z.enum(['cc', 'ach']), paymentTypeAttributes).optional(),
              annual_prepay: z.record(z.enum(['cc', 'ach']), paymentTypeAttributes).optional(),
              fixed_term_single_payment: z
                .record(z.enum(['cc', 'ach']), paymentTypeAttributes)
                .optional(),
            })
            .refine((data) => {
              if ((data.monthly_subs && data.annual_prepay) || data.fixed_term_single_payment) {
                return true;
              }
              return false;
            }, 'Both monthly_subs and annual_prepay must be present or fixed_term_single_payment must be present'),
          itemDetails: z.object({
            itemId: z.string(),
            description: z.string(),
            brand: z.string(),
          }),
        }),
      ),
    }),
  })
  .extend(registrationOnlyResponseSchema.shape);
